import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { io } from 'socket.io-client';
import 'leaflet/dist/leaflet.css';
import CircleIcon from '@mui/icons-material/Circle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useLang, uiText, translateStatus } from '../../utils/i18n';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const SOCKET_URL = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:4000';

const steps = ["Processing", "Accepted", "Packed", "Shipped", "Delivered"];

const LiveTracking = ({ order }) => {
    const { lang } = useLang();
    const [status, setStatus] = useState(order.orderStatus);
    const [driverLocation, setDriverLocation] = useState(
        order.assignedDriver?.currentLocation?.lat ? order.assignedDriver.currentLocation : null
    );

    useEffect(() => {
        const socket = io(SOCKET_URL);
        socket.emit('join-order-room', order._id);

        socket.on('location-update', (loc) => setDriverLocation(loc));
        socket.on('status-update', (data) => setStatus(data.orderStatus));

        return () => socket.disconnect();
    }, [order._id]);

    if (status === "Cancelled") {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center text-red-600 font-medium">
                {uiText('orderCancelledNote', lang)}
            </div>
        );
    }

    const currentIndex = steps.indexOf(status);

    return (
        <div className="bg-white shadow rounded-sm p-6 flex flex-col gap-6">
            <h3 className="font-medium text-lg">{uiText('orderTrackingHeading', lang)}</h3>

            <div className="flex items-center justify-between">
                {steps.map((step, i) => (
                    <div key={step} className="flex-1 flex flex-col items-center relative">
                        {i > 0 && (
                            <div className={`absolute top-2.5 right-1/2 w-full h-0.5 ${i <= currentIndex ? "bg-primary-green" : "bg-gray-200"}`} />
                        )}
                        <span className={`z-10 ${i <= currentIndex ? "text-primary-green" : "text-gray-300"}`}>
                            {i <= currentIndex ? <CircleIcon sx={{ fontSize: "18px" }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: "18px" }} />}
                        </span>
                        <span className={`text-xs mt-1 text-center ${i <= currentIndex ? "text-gray-800 font-medium" : "text-gray-400"}`}>{translateStatus(step, lang)}</span>
                    </div>
                ))}
            </div>

            {status === "Shipped" && order.assignedDriver && (
                <div className="rounded-lg overflow-hidden border" style={{ height: 320 }}>
                    {driverLocation ? (
                        <MapContainer center={[driverLocation.lat, driverLocation.lng]} zoom={14} style={{ height: "100%", width: "100%" }}>
                            <TileLayer
                                attribution='&copy; OpenStreetMap contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={[driverLocation.lat, driverLocation.lng]}>
                                <Popup>{order.assignedDriver.name} {uiText('isOnTheWay', lang)}</Popup>
                            </Marker>
                        </MapContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-sm text-gray-500 bg-gray-50">
                            {uiText('waitingForDriverLocation', lang)}
                        </div>
                    )}
                </div>
            )}

            {order.assignedDriver && (
                <p className="text-sm text-gray-600">
                    {uiText('yourDeliveryDriver', lang)} <span className="font-medium">{order.assignedDriver.name}</span>
                    {order.assignedDriver.vehicleNumber && ` · ${order.assignedDriver.vehicleNumber}`}
                    {order.assignedDriver.phoneNo && ` · ${order.assignedDriver.phoneNo}`}
                </p>
            )}
        </div>
    );
};

export default LiveTracking;
